import { Badge } from '@/components/ui/badge';

interface FlagPillsProps {
  allergies?: string[];
  conditions?: string[];
  max?: number;
}

/**
 * Compact flag pills for a patient row: danger dot-pills for allergies, neutral
 * pills for chronic conditions, with a `+N` overflow chip. Renders nothing when
 * both lists are empty — search-result rows carry no flags (the server returns
 * plaintext only, no decrypted history), so this is the expected case there.
 */
export function FlagPills({ allergies = [], conditions = [], max = 3 }: FlagPillsProps) {
  const items: { tone: 'danger' | 'neutral'; label: string; dot: boolean }[] = [
    ...allergies.map((a) => ({ tone: 'danger' as const, label: a, dot: true })),
    ...conditions.map((c) => ({ tone: 'neutral' as const, label: c, dot: false })),
  ];
  if (items.length === 0) return null;

  const shown = items.slice(0, max);
  const extra = items.length - shown.length;
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {shown.map((it, i) => (
        <Badge key={`${it.label}-${i}`} tone={it.tone} size="sm" dot={it.dot}>
          {it.label}
        </Badge>
      ))}
      {extra > 0 && (
        <Badge tone="neutral" size="sm">
          +{extra}
        </Badge>
      )}
    </div>
  );
}
